import { useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scrollLock';

interface ScreenshotModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  title: string;
}

export const ScreenshotModal = ({ isOpen, onClose, imageUrl, title }: ScreenshotModalProps) => {
  const { toast } = useToast();

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    const isStandalone =
      typeof window !== 'undefined' &&
      ((window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
        // @ts-ignore iOS Safari standalone flag
        (typeof navigator !== 'undefined' && (navigator as any).standalone));

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      if (!isStandalone) {
        lockBodyScroll();
      }
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      if (!isStandalone) {
        unlockBodyScroll();
      }
    };
  }, [isOpen, onClose]);

  const handleDownload = async () => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `screenshot-${new Date().getTime()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({
        title: "Success",
        description: "Screenshot downloaded successfully",
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: "Error",
        description: "Failed to download screenshot",
        variant: "destructive"
      });
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: title,
          text: `Screenshot of ${title}`,
          url: imageUrl,
        });
      } else {
        await navigator.clipboard.writeText(imageUrl);
        toast({
          title: "Copied!",
          description: "Screenshot URL copied to clipboard",
        });
      }
    } catch (error) {
      console.error('Share failed:', error);
      toast({
        title: "Error",
        description: "Failed to share screenshot",
        variant: "destructive"
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="relative max-w-4xl max-h-full w-full h-full flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 p-0"
        >
          <X className="w-5 h-5" />
        </Button>

        <div className="flex-1 flex items-center justify-center mb-20">
          <img loading="lazy" 
            src={imageUrl} 
            alt={title}
            className="max-w-full max-h-[400px] object-contain rounded-lg"
          />
        </div>

        <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-xl p-4 rounded-lg">
          <p className="text-gray-300 mb-4 text-center">
            <span className="text-gray-500">Website: </span>
            {title}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="border-gray-600 text-gray-300 hover:bg-gray-800 bg-transparent"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="border-gray-600 text-gray-300 hover:bg-gray-800 bg-transparent"
            >
              <Share className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
